import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Skeleton } from 'primeng/skeleton';
import { Tag } from 'primeng/tag';
import type { AccountDetailDialogData } from '../account-detail.model';
import {
  accountDetailKind,
  canShowAccountTransactions,
  inventoryTypeToPathType,
} from '../account-path-type';
import { formatAccountLast4, statusTagSeverity } from '../accounts-display';
import { AccountsAnalytics } from '../analytics/accounts-analytics';
import { AccountDetailStore } from './account-detail.store';
import { AccountSummaryMetricsComponent } from './account-summary-metrics.component';
import { mapDebtSummary } from './debt-summary';
import { LinkedAccountsListComponent } from './linked-accounts-list.component';
import { mapLoanSummary } from './loan-summary';
import { mapSavingsSummary } from './savings-summary';
import { compactHeaderMetrics } from './compact-header-metrics';
import { AccountTransactionsComponent } from './account-transactions.component';
import { AccountTransactionsStore } from './account-transactions.store';
import { SpendingSummaryComponent } from './spending-summary.component';
import { mapSpendingSummary } from './spending-summary';

@Component({
  selector: 'bn-account-detail-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Button,
    Dialog,
    Skeleton,
    Tag,
    ScrollRegionComponent,
    SpendingSummaryComponent,
    AccountSummaryMetricsComponent,
    LinkedAccountsListComponent,
    AccountTransactionsComponent,
  ],
  providers: [AccountDetailStore, AccountTransactionsStore],
  templateUrl: './account-detail-dialog.component.html',
  styleUrl: './account-detail-dialog.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AccountDetailDialogComponent {
  private readonly _analytics = inject(AccountsAnalytics);
  readonly store = inject(AccountDetailStore);

  readonly data = input.required<AccountDetailDialogData>();
  readonly visible = model(false);

  readonly statusSeverity = statusTagSeverity;
  readonly formatLast4 = formatAccountLast4;

  readonly kind = computed(() =>
    accountDetailKind(this.data().inventoryType || this.store.payload()?.type || '')
  );
  readonly showTransactions = computed(() =>
    canShowAccountTransactions(this.data().inventoryType || this.store.payload()?.type || '')
  );
  readonly spending = computed(() => {
    const payload = this.store.payload();
    return payload && this.kind() === 'checking' ? mapSpendingSummary(payload) : null;
  });
  readonly savings = computed(() => {
    const payload = this.store.payload();
    return payload && this.kind() === 'savings' ? mapSavingsSummary(payload) : null;
  });
  readonly debt = computed(() => {
    const payload = this.store.payload();
    return payload && this.kind() === 'debt' ? mapDebtSummary(payload) : null;
  });
  readonly loan = computed(() => {
    const payload = this.store.payload();
    return payload && this.kind() === 'loan' ? mapLoanSummary(payload) : null;
  });

  readonly title = computed(() => this.store.payload()?.name ?? this.data().name);
  readonly status = computed(() => this.store.payload()?.status ?? '');
  readonly subline = computed(() => {
    const payload = this.store.payload();
    if (!payload) {
      return '';
    }
    const last4 = formatAccountLast4(payload.idMasked);
    return `${payload.institution} ${last4} • ${payload.type}`;
  });
  readonly compactMetrics = computed(() =>
    compactHeaderMetrics(
      this.kind(),
      this.spending(),
      this.savings()?.metrics ?? null,
      this.debt(),
      this.loan()
    )
  );
  readonly detailsCollapsed = signal(false);

  private readonly _scrollRegion = viewChild(ScrollRegionComponent);
  private readonly _detailsSentinel = viewChild<ElementRef<HTMLElement>>('detailsSentinel');

  private _loggedKey: string | null = null;
  private _loadedKey: string | null = null;

  constructor() {
    effect(() => {
      const data = this.data();
      const open = this.visible();
      if (!open) {
        return;
      }
      const key = `${data.guid}:${data.inventoryType}:${data.id}`;
      if (this._loadedKey === key) {
        return;
      }
      this._loadedKey = key;
      untracked(() => this._load(data));
    });

    effect(() => {
      const payload = this.store.payload();
      const loading = this.store.loading();
      const error = this.store.error();
      const data = this.data();
      if (!payload || loading || error) {
        return;
      }
      const key = `${data.guid}:${payload.id}`;
      untracked(() => {
        if (this._loggedKey === key) {
          return;
        }
        this._loggedKey = key;
        this._analytics.detailOpened(data.inventoryType);
      });
    });

    effect((onCleanup) => {
      const open = this.visible();
      const ready = !this.store.loading() && !this.store.error() && !!this.store.payload();
      const scroll = this._scrollRegion();
      const sentinel = this._detailsSentinel();
      if (
        !open ||
        !ready ||
        !scroll ||
        !sentinel ||
        typeof IntersectionObserver === 'undefined'
      ) {
        this.detailsCollapsed.set(false);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const past =
            !!entry &&
            !entry.isIntersecting &&
            entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0);
          this.detailsCollapsed.set(past);
        },
        { root: scroll.viewportElement, threshold: 0 }
      );
      observer.observe(sentinel.nativeElement);
      onCleanup(() => {
        observer.disconnect();
        this.detailsCollapsed.set(false);
      });
    });
  }

  close(): void {
    this.visible.set(false);
  }

  async retry(): Promise<void> {
    await this._load(this.data());
  }

  private _load(data: AccountDetailDialogData): Promise<void> {
    const pathType = inventoryTypeToPathType(data.inventoryType);
    if (!pathType) {
      return Promise.resolve();
    }
    return this.store.load(data.guid, pathType, data.id);
  }
}
